// Content-completeness ("avisos") aggregator. Pure logic: given a user's tours
// (plus their translatable children), it derives the list of things still
// missing — untranslated names/hotspots, empty/unpublished tours. Computed
// live (no DB state), so an issue disappears the moment the owner fills the gap.
//
// Single source of the completeness rules. The health hook feeds it data; the
// panel renders `HealthIssue[]`. Reuses the same i18n-gap primitives the editor
// already uses (missingNameLangs / missingContentLangs).
import type { Insight, InsightSeverity } from "@u/businessInsights";
import type { HotspotTranslations, HotspotType, VFloor, VRoom, VTour } from "@/types";
import { buildRoute } from "@u/routes";
import { areaFromEdges, isClosablePolygon } from "@u/roomShape";
import { missingContentLangs, missingNameLangs } from "@u/nameTranslations";
import { tourLanguages } from "@u/tourLanguages";

/** El producto + la capa de negocio (leads/contactos/reservas/tareas), cuyos
 *  avisos son señales de gestión más que huecos de contenido. */
export type HealthProduct = "360" | "business";

export interface HealthIssue {
  /** Stable dedup key (resource + code + optional discriminator). */
  id: string;
  product: HealthProduct;
  resourceId: string;
  resourceTitle: string;
  code: string;
  /** critical = visitor-facing broken/invisible; warning = incomplete; info = optional. */
  severity: "critical" | "warning" | "info";
  /** i18n interpolation payload (langs joined, counts, field label). */
  vars?: Record<string, string | number>;
  /** Message key override. Default is `health.msg.<code>`; business avisos reuse
   *  the insight copy (`insight_*_title`) instead of duplicating it. */
  msgKey?: string;
  /** Deep-link to the editor spot that fixes it. */
  href: string;
}

/** Minimal hotspot shape the health check needs (subset of VHotspot). */
export interface HealthHotspot {
  id: string;
  scene_id: string;
  type: HotspotType;
  target_scene_id: string | null;
  title: string | null;
  description: string | null;
  translations: HotspotTranslations | null;
  /** Resolved media sources — an uploaded file's url, or the embed link
   *  (external_url) for iframe types. A media hotspot with none set opens
   *  nothing when clicked. Optional: only media types carry them. */
  img_url?: string | null;
  audio_url?: string | null;
  video_url?: string | null;
  external_url?: string | null;
}

/** True when a media hotspot (Img/Audio/Video/iframe) has NO source behind it
 *  — the marker renders but the click opens an empty viewer. Uses resolved urls
 *  so it's source-agnostic (uploaded file vs embed link). Non-media types → false
 *  (their emptiness is handled separately: Info by text, Nav by target). */
function mediaHotspotEmpty(h: HealthHotspot): boolean {
  switch (h.type) {
    case "Img":
      return !h.img_url;
    case "Audio":
      return !h.audio_url;
    case "Video":
      return !h.video_url && !h.external_url;
    case "VideoIframe":
      return !h.external_url && !h.video_url;
    case "ImgIframe":
      return !h.external_url && !h.img_url;
    default:
      return false;
  }
}

/** Which hotspot types carry visitor-visible translatable text. */
function hotspotI18nKind(type: HotspotType): "full" | "title" | null {
  if (type === "Info" || type === "Area" || type === "Img") return "full";
  if (
    type === "ImgIframe" ||
    type === "VideoIframe" ||
    type === "Video" ||
    type === "Audio"
  )
    return "title";
  return null; // Nav / NavArrow / NavPreview / FloorMap — no visitor text
}

function langList(codes: string[]): string {
  return codes.map((c) => c.toUpperCase()).join(", ");
}

/** Append the aviso code as a `?focus=` deep-link anchor. The editor rings the
 *  matching `[data-health-focus="<code>"]` element (see useHealthFocus); no-op
 *  when the field isn't tagged, so the link still lands on the right section. */
function withFocus(href: string, code: string): string {
  return `${href}${href.includes("?") ? "&" : "?"}focus=${code}`;
}


export interface TourChildren {
  rooms: VRoom[];
  floors: VFloor[];
  hotspots: HealthHotspot[];
  /** All scene ids of the tour — used to detect nav hotspots pointing nowhere. */
  sceneIds: string[];
}

/** Issues for one tour. `hotspots` are the tour's hotspots (any scene). */
export function tourIssues(tour: VTour, children: TourChildren): HealthIssue[] {
  const issues: HealthIssue[] = [];
  const primary = tour.language;
  const offered = tourLanguages(tour.settings, primary);
  const title = tour.title || tour.short_id;
  // Deep-link each aviso to the editor section that fixes it (`?section=`).
  const add = (
    code: string,
    severity: HealthIssue["severity"],
    section: string,
    vars?: HealthIssue["vars"],
    disc = "",
  ) =>
    issues.push({
      id: `360:${tour.id}:${code}${disc}`,
      product: "360",
      resourceId: tour.id,
      resourceTitle: title,
      code,
      severity,
      vars,
      href: withFocus(buildRoute.edit(tour.id, section), code),
    });

  // Empty / unpublished — the loudest "won't show anything" cases.
  // Use sceneIds (all scenes via v_scenes.tour_id), NOT tour.scene_count —
  // scene_count only counts scenes placed in a room/floor (inner join in
  // v_tours), so an unplaced-scene tour would false-flag as empty.
  const sceneCount = children.sceneIds.length;
  if (sceneCount === 0) add("tour_empty", "critical", "structure");
  else {
    if (tour.visibility === "public" && tour.state !== "published")
      add("tour_unpublished", "info", "configuration");
    // Published multi-scene tour with no navigation hotspots = dead-end: the
    // visitor lands and can't move between scenes.
    const navHotspots = children.hotspots.filter(
      (h) =>
        h.type === "Nav" ||
        h.type === "NavArrow" ||
        h.type === "NavSign" ||
        h.type === "NavPreview",
    );
    if (tour.state === "published" && sceneCount > 1 && navHotspots.length === 0)
      add("tour_dead_end", "critical", "structure");
    // Nav hotspot whose target scene was deleted → visitor clicks, goes nowhere.
    const sceneSet = new Set(children.sceneIds);
    const brokenNav = navHotspots.filter(
      (h) => h.target_scene_id != null && !sceneSet.has(h.target_scene_id),
    ).length;
    if (brokenNav > 0)
      add("tour_broken_nav", "critical", "structure", { count: brokenNav });
    // Has content but no cover → the card/viewer shows a "No_Signal" placeholder.
    if (!tour.cover_url) add("tour_no_cover", "warning", "configuration");
  }

  // Active hotspots with nothing behind them — the marker renders but the click
  // does nothing. Conservative: an Info popup with no text at all, a Nav that was
  // never pointed at a scene, or a media hotspot (Img/Audio/Video/iframe) with no
  // source. Area is excluded (it can inherit content via content_ref_id).
  const emptyHotspots = children.hotspots.filter((h) => {
    if (
      h.type === "Nav" ||
      h.type === "NavPreview" ||
      h.type === "NavArrow" ||
      h.type === "NavSign"
    )
      return h.target_scene_id == null;
    if (h.type === "Info") {
      const anyTitle =
        h.title?.trim() ||
        Object.values(h.translations ?? {}).some((v) => v?.title?.trim());
      const anyDesc =
        h.description?.trim() ||
        Object.values(h.translations ?? {}).some((v) => v?.description?.trim());
      return !anyTitle && !anyDesc;
    }
    return mediaHotspotEmpty(h);
  }).length;
  if (emptyHotspots > 0)
    add("hotspot_no_content", "warning", "structure", { count: emptyHotspots });

  // Tour title i18n gap.
  const titleGap = missingNameLangs(tour.translations, offered, primary);
  if (titleGap.length)
    add("tour_name_i18n", "warning", "configuration", { langs: langList(titleGap) });

  // Room / floor name i18n gaps — one issue per product×code, aggregated count.
  const roomGap = children.rooms.filter(
    (r) => (r.name?.trim()?.length ?? 0) > 0 &&
      missingNameLangs(r.translations, offered, primary).length > 0,
  );
  if (roomGap.length) {
    const langs = new Set<string>();
    roomGap.forEach((r) =>
      missingNameLangs(r.translations, offered, primary).forEach((l) =>
        langs.add(l),
      ),
    );
    add("room_i18n", "warning", "structure", {
      count: roomGap.length,
      langs: langList([...langs]),
    });
  }

  // Measured drift: a room's declared plan sides imply an area that disagrees
  // (>15%, past the fit's approximation) with the stored mts2 — which may have
  // been set manually or from the viewer. One aggregated aviso; re-saving the
  // plan (which writes the computed area) or the values clears it.
  const driftRooms = children.rooms.filter((r) => {
    if (!r.mts2 || r.mts2 <= 0 || !isClosablePolygon(r.shape) || !r.edge_lengths)
      return false;
    const area = areaFromEdges(r.shape!, r.edge_lengths);
    return area != null && Math.abs(area - r.mts2) / r.mts2 > 0.15;
  }).length;
  if (driftRooms > 0)
    add("room_measure_drift", "warning", "structure", { count: driftRooms });
  const floorGap = children.floors.filter(
    (f) => (f.name?.trim()?.length ?? 0) > 0 &&
      missingNameLangs(f.translations, offered, primary).length > 0,
  );
  if (floorGap.length) {
    const langs = new Set<string>();
    floorGap.forEach((f) =>
      missingNameLangs(f.translations, offered, primary).forEach((l) =>
        langs.add(l),
      ),
    );
    add("floor_i18n", "warning", "structure", {
      count: floorGap.length,
      langs: langList([...langs]),
    });
  }

  // Hotspot title/description i18n gaps — aggregate across scenes.
  const langs = new Set<string>();
  let hotspotCount = 0;
  for (const hs of children.hotspots) {
    const kind = hotspotI18nKind(hs.type);
    if (!kind) continue;
    const gap = missingContentLangs(
      hs.translations,
      offered,
      primary,
      { title: hs.title, description: hs.description },
      kind === "full",
    );
    if (gap.length) {
      hotspotCount += 1;
      gap.forEach((l) => langs.add(l));
    }
  }
  if (hotspotCount)
    add("hotspot_i18n", "warning", "structure", {
      count: hotspotCount,
      langs: langList([...langs]),
    });

  return issues;
}

// ── Business avisos ───────────────────────────────────────────────────────
// The opportunity engine (businessInsights) already produces the cross-product
// signals — stale leads, pending bookings, cooling contacts. Avisos is the
// one place the owner looks for "what's pending", so those signals are mapped
// into the same HealthIssue shape instead of living only in analytics.
// The insight copy is reused verbatim via `msgKey`, no duplicate i18n.

/** Which collection an insight belongs to → the group header + its i18n key. */
const INSIGHT_AREA: Record<string, { id: string; titleKey: string }> = {
  leads_stale: { id: "leads", titleKey: "leads" },
  conversion_drop: { id: "leads", titleKey: "leads" },
  contacts_cooling: { id: "contacts", titleKey: "crm_contacts_title" },
  bookings_pending: { id: "bookings", titleKey: "bookings" },
  tour_traffic_no_leads: { id: "tours", titleKey: "tours_nav" },
  properties_no_interest: { id: "properties", titleKey: "properties_title" },
};

const INSIGHT_SEVERITY: Record<InsightSeverity, HealthIssue["severity"]> = {
  urgent: "critical",
  warning: "warning",
  opportunity: "info",
};

/** Map opportunity insights to avisos. `label` translates the group header
 *  (passed in so this module stays React-free and i18n-free). */
export function businessIssues(
  insights: Insight[],
  label: (key: string) => string,
): HealthIssue[] {
  return insights.map((ins) => {
    const area = INSIGHT_AREA[ins.id] ?? { id: "business", titleKey: "analytics" };
    return {
      id: `business:${ins.id}`,
      product: "business" as const,
      resourceId: `business:${area.id}`,
      resourceTitle: label(area.titleKey),
      code: ins.id,
      severity: INSIGHT_SEVERITY[ins.severity],
      vars: { count: ins.count, name: ins.name ?? "" },
      msgKey: ins.titleKey,
      href: ins.to,
    };
  });
}

const SEVERITY_ORDER: Record<HealthIssue["severity"], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/** Flatten + sort (critical → warning → info, then by product) for the panel. */
export function aggregateHealth(
  tours: { tour: VTour; children: TourChildren }[],
  business: HealthIssue[] = [],
): HealthIssue[] {
  const all: HealthIssue[] = [
    ...tours.flatMap((t) => tourIssues(t.tour, t.children)),
    ...business,
  ];
  return all.sort(
    (a, b) =>
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
      a.product.localeCompare(b.product) ||
      a.resourceTitle.localeCompare(b.resourceTitle),
  );
}

/** Group by resource for the panel's sectioned list. */
export function groupByResource(issues: HealthIssue[]): {
  key: string;
  product: HealthProduct;
  resourceId: string;
  resourceTitle: string;
  issues: HealthIssue[];
}[] {
  const map = new Map<string, HealthIssue[]>();
  for (const i of issues) {
    const key = `${i.product}:${i.resourceId}`;
    (map.get(key) ?? map.set(key, []).get(key)!).push(i);
  }
  return [...map.entries()].map(([key, list]) => ({
    key,
    product: list[0].product,
    resourceId: list[0].resourceId,
    resourceTitle: list[0].resourceTitle,
    issues: list,
  }));
}
